import React, { useEffect, useState, useCallback } from "react";
import { ShieldWarning, ShieldCheck, X, Check, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

/**
 * Banner shown to a self-signup athlete who is under 18 and whose guardian
 * has not yet confirmed the account. Explains the limited-access state and
 * lets them re-send the request to a (different) guardian email.
 */
export function ConsentPendingBanner() {
  const [status, setStatus] = useState(null);
  const [editing, setEditing] = useState(false);
  const [gEmail, setGEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/me/guardian-consent-status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!status || !status.locked) return null;

  const resend = async () => {
    if (!gEmail.trim()) { toast.error("Enter a guardian email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/me/guardian-consent/resend", { guardian_email: gEmail.trim() });
      toast.success(data.guardian_has_account
        ? "Request sent — your guardian has been notified."
        : "Saved. Ask your guardian to sign up with that email to approve.");
      setEditing(false);
      setGEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 border border-amber-400/40 bg-amber-400/5 rounded-sm p-4" data-testid="consent-pending-banner">
      <div className="flex items-start gap-3">
        <ShieldWarning size={22} weight="fill" className="text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm text-amber-400">
            {status.request_status === "expired"
              ? "CONSENT REQUEST EXPIRED"
              : status.request_status === "declined"
                ? "GUARDIAN DECLINED"
                : "GUARDIAN CONFIRMATION NEEDED"}
          </div>
          <p className="text-[12px] text-text-secondary leading-snug mt-1">
            {status.request_status === "expired" ? (
              <>
                We didn't hear back from <b className="text-white/80">{status.guardian_email}</b>, so that
                request expired. Your account stays limited — <b className="text-white/80">coach linking,
                messaging and team leaderboards stay locked</b>. Add a different parent or guardian's email to try again.
              </>
            ) : status.request_status === "declined" ? (
              <>
                <b className="text-white/80">{status.guardian_email}</b> declined the request. Your account
                stays limited — <b className="text-white/80">coach linking, messaging and team leaderboards
                stay locked</b>. Add a different parent or guardian's email to try again.
              </>
            ) : (
              <>
                You're under 18, so a parent or guardian must confirm your account. You can set up your profile
                and log your own training now — <b className="text-white/80">coach linking, messaging and team leaderboards
                stay locked</b> until they approve. We asked <b className="text-white/80">{status.guardian_email}</b>.
              </>
            )}
          </p>
          {!editing ? (
            <button
              onClick={() => { setEditing(true); setGEmail(status.request_status === "pending" ? (status.guardian_email || "") : ""); }}
              className="mt-2 label-tiny text-brand-accent hover:underline"
              data-testid="consent-change-guardian-button"
            >
              {status.request_status === "pending" ? "USE A DIFFERENT GUARDIAN EMAIL" : "ADD A GUARDIAN EMAIL"}
            </button>
          ) : (
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={gEmail}
                onChange={(e) => setGEmail(e.target.value)}
                placeholder="guardian@example.com"
                className="bg-surface-elevated border-white/10 flex-1"
                data-testid="consent-guardian-email-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={resend}
                  disabled={busy}
                  className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50"
                  data-testid="consent-resend-button"
                >
                  {busy ? "SENDING…" : "SEND"}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-3 py-2 text-xs label-tiny border border-white/15 text-text-secondary hover:border-white/40"
                >
                  CANCEL
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Card listing pending under-18 consent requests addressed to the logged-in
 * user (the guardian). Lets them approve or decline. Renders nothing when
 * there are no pending requests.
 */
export function GuardianConsentRequests() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/me/guardian-consent-requests")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (items.length === 0) return null;

  const act = async (linkId, action) => {
    setBusyId(linkId);
    try {
      await api.post(`/me/guardian-consent-requests/${linkId}/${action}`);
      toast.success(action === "approve" ? "Account approved" : "Request declined");
      setItems((prev) => prev.filter((i) => i.link_id !== linkId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="tactical-card p-6 space-y-4" data-testid="guardian-consent-requests">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} weight="duotone" className="text-brand-accent" />
        <h3 className="font-heading text-lg">GUARDIAN CONSENT REQUESTS</h3>
      </div>
      <p className="text-[11px] text-text-secondary leading-snug">
        A young athlete listed your email as their parent/guardian. Approving confirms you consent to them
        using Edge Training and unlocks coach linking, messaging and leaderboards for them.
      </p>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.link_id} className="border border-white/10 p-3 flex items-center justify-between gap-3" data-testid={`consent-request-${r.link_id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{r.athlete_name || "Athlete"}</div>
              <div className="text-[11px] text-text-secondary truncate">
                {r.athlete_email}{r.athlete_age != null ? ` · age ${r.athlete_age}` : ""}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => act(r.link_id, "approve")}
                disabled={busyId === r.link_id}
                className="btn-kinetic px-3 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`consent-approve-${r.link_id}`}
              >
                <Check size={13} weight="bold" /> APPROVE
              </button>
              <button
                onClick={() => act(r.link_id, "decline")}
                disabled={busyId === r.link_id}
                className="px-3 py-2 text-xs label-tiny border border-red-500/40 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`consent-decline-${r.link_id}`}
              >
                <X size={13} weight="bold" /> DECLINE
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * Lists the caller's granted guardian-consent records with a dated-PDF download.
 * Renders nothing when there are no granted records.
 */
export function ConsentRecords() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get("/me/consent-audit")
      .then(({ data }) => setItems((data.items || []).filter((r) => r.event === "granted")))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  const downloadPdf = async (r) => {
    setBusyId(r.audit_id);
    try {
      const res = await api.get(`/me/consent-audit/${r.audit_id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "edge-training-consent-record.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the consent record");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="tactical-card p-6 space-y-4" data-testid="consent-records">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} weight="duotone" className="text-brand-accent" />
        <h3 className="font-heading text-lg">CONSENT RECORDS</h3>
      </div>
      <p className="text-[11px] text-text-secondary leading-snug">
        Dated proof of guardian consent. Download a PDF copy for your records.
      </p>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.audit_id} className="border border-white/10 p-3 flex items-center justify-between gap-3" data-testid={`consent-record-${r.audit_id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{r.athlete_name || r.athlete_email || "Athlete"}</div>
              <div className="text-[11px] text-text-secondary truncate">
                Granted {new Date(r.created_at).toLocaleDateString()}
                {r.guardian_email ? ` · by ${r.guardian_name || r.guardian_email}` : ""}
              </div>
            </div>
            <button
              onClick={() => downloadPdf(r)}
              disabled={busyId === r.audit_id}
              className="flex-shrink-0 inline-flex items-center gap-1.5 border border-white/15 hover:border-brand-accent hover:text-brand-accent transition-colors px-3 py-2 text-xs label-tiny tracking-widest disabled:opacity-50"
              data-testid={`consent-pdf-${r.audit_id}`}
            >
              <FilePdf size={14} weight="bold" /> {busyId === r.audit_id ? "…" : "PDF"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
